import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function Economics42() {

   const contentRef = useRef(null);
         const links = {
        prev: "/economics-41", 
        index: "/index-economics",                 
        next: "/economics-43", 
      };
        const titleText ="The Concept of Time Preference";

        const questions = [
  {
    text: "1. What is meant by the concept of time preference in economics?",
    options: [
      {
        value: "A",
        label: "A. The willingness to trade one currency for another",
      },
      {
        value: "B",
        label:
          "B. The ratio of value placed on immediate consumption versus future consumption",
      },
      {
        value: "C",
        label:
          "C. The tendency of governments to regulate monetary systems",
      },
      {
        value: "D",
        label:
          "D. The ability of societies to innovate technologically",
      },
    ],
    correct: "B",
  },
  {
    text: "2. Which factor most directly contributes to lowering time preference in a society?",
    options: [
      { value: "A", label: "A. Political instability" },
      {
        value: "B",
        label:
          "B. Availability of durable consumer goods",
      },
      {
        value: "C",
        label:
          "C. Enforcing property rights and contracts",
      },
      {
        value: "D",
        label:
          "D. Shorter average life expectancy",
      },
    ],
    correct: "C",
  },
  {
    text: "3. Why has gold historically replaced other forms of money like copper, iron, or shells?",
    options: [
      {
        value: "A",
        label: "A. Gold was easier to produce in large amounts",
      },
      {
        value: "B",
        label:
          "B. Gold was harder to produce relative to demand, maintaining its stability",
      },
      {
        value: "C",
        label:
          "C. Gold was more aesthetically pleasing to societies",
      },
      {
        value: "D",
        label:
          "D. Gold could be consumed as a good in times of scarcity",
      },
    ],
    correct: "B",
  },
  {
    text: "4. How does Bitcoin differ from both gold and fiat money in influencing time preference?",
    options: [
      {
        value: "A",
        label:
          "A. It can be produced rapidly when demand rises",
      },
      {
        value: "B",
        label:
          "B. Its supply is absolutely capped by code, preventing inflation",
      },
      {
        value: "C",
        label:
          "C. It is controlled by governments to ensure financial stability",
      },
      {
        value: "D",
        label:
          "D. It deteriorates over time like durable goods",
      },
    ],
    correct: "B",
  },
];

  
    // State to track selected answers for all questions
        const [answers, setAnswers] = useState({});
      
        // Function to update an answer
        const handleAnswerChange = (questionIndex, selectedValue) => {
          setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
        };
      
        // Compute score
        const score = questions.reduce((total, q, index) => {
          return total + (answers[index] === q.correct ? 1 : 0);
        }, 0);
  return (
    <div className="container">
    <LandingSection
              titleText={titleText}
              scrollToRef={contentRef}
              progressValue={42}
              timerMinutes={60}
              currentLevel={2}
              unlockedBadges={3}
            />
        
  
      <p className="content" ref={contentRef}>
        Welcome to <b> Day 42</b>!
Today we will explore the idea of time preference. This is a fundamental concept in economics that shapes how individuals and societies make choices between consuming today or saving for tomorrow. Time preference reflects how much value we place on the present versus the future. A high time preference means prioritizing immediate consumption and gratification, while a low time preference means valuing patience, savings, and long-term planning. Understanding this concept is key to seeing how money influences human behavior, how civilizations grow, and why sound money like gold in the past and Bitcoin today can lower time preference and encourage sustainable prosperity.
      <br/><br/><b>The Nature of Time Preference</b><br/><br/>
      
      Scarcity of time is the foundation of all economic choices. No matter how many resources humans may acquire, the hours in a day and the years of one’s life remain limited. This natural constraint forces people to prioritize between present consumption and future consumption. Economists call this prioritization “time preference.” Time preference is essentially the ratio of value that a person places on enjoying goods and services immediately compared to postponing their use for the future. A universal law of human action is that time preference is always positive, meaning that people always value immediate consumption to some degree. Yet the degree of this preference varies greatly from person to person, and even within the same individual across different stages of life.<br/>
      Several factors influence the strength of an individual’s time preference. Biological development is one of the most obvious. A young child, for instance, usually exhibits a very high time preference and seeks immediate gratification, unable or unwilling to defer rewards. As the child matures into adulthood, the ability to plan, defer, and invest in the future strengthens, leading to a fall in time preference. Adults typically think about education, careers, and family security, which requires them to sacrifice present consumption for the promise of greater benefits in the future. Yet as humans enter old age, time preference may rise again, because uncertainty about life expectancy encourages them to prioritize enjoying life in the present.<br/>
      In addition to biological factors, social and institutional structures shape how time preference evolves. Cultures that emphasize saving, education, and long-term investment encourage lower time preference. Stable institutions that enforce property rights and uphold contracts give individuals greater confidence that their deferred consumption will not be lost or stolen, thus incentivizing saving. On the other hand, environments of political instability, corruption, or frequent economic crises erode trust in the future, pushing people to raise their time preference and consume today rather than save for tomorrow. Even external events such as wars, famines, or pandemics have historically led to sharp shifts in time preference, often moving entire populations toward shorter-term thinking.<br/>
      The central lesson is that time preference not only varies between individuals but also has a profound impact on civilizations. Societies dominated by high time preference tend to consume excessively in the present and fail to build for the future, often leading to economic instability and stagnation. Conversely, societies where lower time preference prevails are more likely to see capital accumulation, technological innovation, and long-term prosperity. Time preference, therefore, is not just a personal economic choice but a powerful driver of cultural and historical development.<br/><br/>
      <b>Money, Savings, and Civilizational Stability</b> <br/><br/>
      One of the most powerful ways humans reduce their time preference is by saving in durable goods. Durable goods such as tools, homes, and machinery provide future security by allowing individuals to defer consumption today in exchange for greater stability tomorrow. However, durable goods present a significant problem in the form of the “coincidence of wants.” For example, a person who saves by holding livestock may later find that they cannot easily trade those animals for food or medicine when they need it most. Durable goods also deteriorate, can be bulky, and may not be widely accepted in trade.<br/>
      This is where money comes into play as one of the greatest human inventions. Money eliminates the problem of coincidence of wants by serving as a universal medium of exchange. More importantly, money allows savings to be liquid, portable, and widely acceptable across different contexts. The ability to store value in money lowers time preference because individuals no longer need to worry about whether their future needs will match the specific goods they saved. They can instead hold money and be confident they can trade it later for whatever is necessary.<br/>
However, not all forms of money are equally effective in lowering time preference. The key determinant is whether the supply of money can be easily increased. If the supply of a given form of money can be rapidly expanded when demand rises, then its value will inevitably fall. Civilizations that saved in such “easy money” often found that their savings lost value over time, leading to instability. By contrast, money that is hard to produce in proportion to rising demand retains its purchasing power. This is why history has repeatedly witnessed the replacement of primitive forms of money such as seashells, beads, copper, or iron with more reliable forms like gold and silver.<br/>
   Thus, the evolution of money is intimately connected to the evolution of time preference. Civilizations that relied on hard money nurtured cultures of saving, investment, and long-term growth. On the other hand, those that relied on easy money tended to suffer instability, inflation, and societal decay. Money, therefore, is not merely a tool for exchange but a fundamental force in shaping the trajectory of societies.          
            <br/><br/><b>Bitcoin and the Future of Time Preference</b>
<br/><br/>          In the twenty-first century, the emergence of Bitcoin has introduced a revolutionary development in the history of money. For the first time, humans have access to a digital form of money whose supply is absolutely capped. Unlike gold, silver, or government-issued fiat currencies, Bitcoin cannot be inflated or produced in greater quantities in response to rising demand. Its scarcity is guaranteed by its code, and it is resistant to censorship, meaning that no government or institution can unilaterally block transactions or seize it without consent.<br/>  
            Bitcoin directly addresses many of the uncertainties that drive people toward higher time preference. Wars, inflationary policies, and political instability have often forced people to spend quickly rather than save for the future. Governments that inflate currency erode the value of savings, punishing savers while rewarding short-term consumption. In contrast, Bitcoin provides an avenue for saving that is immune to these forces. People across the world who have adopted Bitcoin as a savings tool report greater confidence in their financial future, as they know their savings are protected from both inflation and arbitrary confiscation.<br/>
           Historical patterns suggest that civilizations saving in hard money tend to achieve prosperity and technological progress. If Bitcoin continues to be adopted widely, it has the potential to replicate this dynamic on a global scale. Unlike gold, Bitcoin is easily transportable across borders, divisible into extremely small units, and verifiable instantly without the need for trusted intermediaries. These characteristics make it superior not only to past forms of hard money but also to the unstable fiat currencies that dominate the present.<br/>
              <br />By encouraging people to save in a hard and incorruptible form of money, Bitcoin may significantly reduce global time preference. Lower time preference means individuals and societies would be more inclined to plan for the future, accumulate capital, invest in long-term projects, and develop technologies that advance human civilization. Just as the gold standard coincided with one of the most innovative and prosperous eras in history, Bitcoin has the potential to inaugurate a new age of financial stability and growth. While challenges such as regulatory hurdles, technological risks, and volatility remain, the underlying principles of Bitcoin align closely with the historical lessons of hard money and time preference.<br/>
              In conclusion, the rise of Bitcoin represents more than just a financial innovation. It signals a possible transformation in how societies think about savings, consumption, and the future. If widely adopted, Bitcoin could foster lower time preference globally, enabling humanity to build a more secure, stable, and prosperous future.
        <br /><br/>
          <b>Summary of Today’s Lesson</b> <br/><br/>
          In summary, time preference is about how we weigh the present against the future in our decisions. A high time preference focuses on immediate rewards, often at the cost of long-term well-being, while a low time preference encourages patience, savings, and investment in the future. Societies with a lower time preference tend to build stronger families, healthier economies, and lasting institutions, while those with higher time preference often fall into short-term thinking and instability. By understanding and adjusting our own time preferences, we can make choices that not only benefit our lives today but also create a foundation for greater prosperity in the future.
         <br/><br/> <b> Coming Up Next</b>
        <br />
        <br />
        In the next lesson, we will explore the concept of money itself. We will look at why some goods naturally became money throughout history and what made them more suitable than others. You will learn about the idea of “saleability,” how easily something can be exchanged in the market, and why this quality determined which goods survived as money. This will help you understand why money is not just chosen but discovered through human interaction and trade.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
      <Question
        key={index}
        questionText={q.text}
        options={q.options}
        correctAnswer={q.correct}
        onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
      />
    ))}
    
           <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
            Score: {score} / {questions.length}
          </div>
   
      <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default Economics42;
